import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Users, Grid3x3, Map, Move, Save, History, CalendarClock, Sparkles, Pencil, Trash2, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, Field, EmptyState } from "@/components/common";
import { SellBlocked } from "@/components/TrialBanner";
import { useAuth } from "@/context/AuthContext";
import { api, brl, errMsg, fmtTime, fmtDateTime, isManagerRole } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const STATUS = {
  livre: ["Livre", "border-emerald-500/60 bg-emerald-500/10", "bg-emerald-500"],
  ocupada: ["Ocupada", "border-red-500/60 bg-red-500/10", "bg-red-500"],
  reservada: ["Reservada", "border-amber-500/60 bg-amber-500/10", "bg-amber-500"],
  conta: ["Aguardando pagamento", "border-blue-500/60 bg-blue-500/10", "bg-blue-500"],
  limpeza: ["Em limpeza", "border-violet-500/60 bg-violet-500/10", "bg-violet-500"],
};
const pos = (t, i) => ({ x: t.x ?? 4 + (i % 6) * 16, y: t.y ?? 6 + Math.floor(i / 6) * 22 });
const stateOf = (t) => (t.comanda ? (t.status === "conta" ? "conta" : "ocupada") : t.status in STATUS ? t.status : "livre");

function TableTile({ t, onClick, style, editing, onPointerDown }) {
  const [label, cls, dot] = STATUS[stateOf(t)];
  return (
    <button onClick={onClick} onPointerDown={onPointerDown} style={style} data-testid={`table-card-${t.number}`}
      className={cn("card-surface relative overflow-hidden [&>*:not(:first-child)]:relative border-2 p-3 text-left flex flex-col select-none hover:z-10 bg-card", cls.split(" ")[0], t.shape === "round" && "rounded-full items-center justify-center text-center", editing ? "cursor-move touch-none ring-2 ring-primary/40" : "card-hover")}>
      <span className={cn("pointer-events-none absolute inset-0", cls.split(" ")[1])} />
      <div className="relative flex items-center justify-between w-full gap-1">
        <span className="font-display text-2xl font-bold">{String(t.number).padStart(2, "0")}</span>
        {t.shape !== "round" && <span className={cn("h-3 w-3 rounded-full", dot)} />}
      </div>
      {t.name && <span className="text-[11px] font-medium truncate max-w-full">{t.name}</span>}
      <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Users className="h-3 w-3" />{t.seats}</span>
      <span className="mt-auto text-[11px] font-semibold" data-testid={`table-status-badge-${t.number}`}>{t.joined ? "Junta" : label}</span>
      {t.comanda && <span className="font-mono text-xs font-bold">{brl(t.comanda.total)} · {fmtTime(t.comanda.opened_at)}</span>}
      {t.status === "reservada" && t.reservation && <span className="text-[10px] truncate max-w-full">{t.reservation.name} {fmtTime(t.reservation.at)}</span>}
    </button>
  );
}

function FloorPlan({ tables, editing, setTables, onClick }) {
  const ref = useRef(null);
  const drag = useRef(null);
  const move = (e) => {
    if (!drag.current) return;
    const r = ref.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(88, ((e.clientX - r.left) / r.width) * 100 - drag.current.dx));
    const y = Math.max(0, Math.min(82, ((e.clientY - r.top) / r.height) * 100 - drag.current.dy));
    setTables((ts) => ts.map((t) => (t.id === drag.current.id ? { ...t, x, y } : t)));
  };
  return (
    <div ref={ref} onPointerMove={move} onPointerUp={() => (drag.current = null)} onPointerLeave={() => (drag.current = null)}
      className={cn("relative w-full h-[560px] rounded-xl border bg-muted/30 overflow-hidden", editing && "grain")} data-testid="floor-plan">
      {tables.map((t, i) => {
        const p = pos(t, i);
        return (
          <TableTile key={t.id} t={t} editing={editing} onClick={() => !editing && onClick(t)}
            style={{ position: "absolute", left: `${p.x}%`, top: `${p.y}%`, width: "11%", minWidth: 92, height: t.shape === "round" ? 110 : 118 }}
            onPointerDown={(e) => {
              if (!editing) return;
              const r = ref.current.getBoundingClientRect();
              drag.current = { id: t.id, dx: ((e.clientX - r.left) / r.width) * 100 - p.x, dy: ((e.clientY - r.top) / r.height) * 100 - p.y };
            }} />
        );
      })}
    </div>
  );
}

function TableDialog({ t, onClose, reload, tables }) {
  const [name, setName] = useState("");
  const [mode, setMode] = useState(null);
  const [res, setRes] = useState({ name: "", phone: "", at: "", people: 2 });
  const [history, setHistory] = useState([]);
  const navigate = useNavigate();
  useEffect(() => { setMode(null); setName(""); }, [t]);
  if (!t) return null;
  const st = stateOf(t);
  const run = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      onClose();
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const openTable = async () => {
    try {
      const { data } = await api.post(`/tables/${t.id}/open`, { customer_name: name });
      navigate(`/app/comandas/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const showHistory = async () => { setHistory((await api.get(`/tables/${t.id}/history`)).data); setMode("history"); };
  return (
    <Dialog open={!!t} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="open-table-modal">
        <DialogHeader>
          <DialogTitle>Mesa {t.number}{t.name ? ` · ${t.name}` : ""}</DialogTitle>
          <DialogDescription>{STATUS[st][0]} · {t.seats} lugares{t.reservation ? ` · Reserva: ${t.reservation.name} ${fmtDateTime(t.reservation.at)} (${t.reservation.people} pessoas)` : ""}</DialogDescription>
        </DialogHeader>
        {t.comanda ? (
          <div className="space-y-2">
            <p className="text-sm">Comanda #{t.comanda.number} · {t.comanda.customer_name || "—"} · <b className="font-mono">{brl(t.comanda.total)}</b></p>
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={() => navigate(`/app/comandas/${t.comanda.id}`)} data-testid="go-comanda-btn">Abrir atendimento</Button>
              <Button variant="outline" onClick={() => run(() => api.post(`/tables/${t.id}/status`, { status: t.status === "conta" ? "ocupada" : "conta" }), "Status atualizado")} data-testid="toggle-bill-status-btn">{t.status === "conta" ? "Voltar a ocupada" : "Aguardando pagamento"}</Button>
              <Button variant="outline" className="col-span-2" onClick={() => setMode("join")} data-testid="join-table-btn"><Link2 className="h-4 w-4 mr-1" />Juntar com outra mesa</Button>
            </div>
            {mode === "join" && (
              <div className="grid grid-cols-4 gap-2">
                {tables.filter((x) => x.id !== t.id && x.comanda?.id !== t.comanda.id).map((x) => (
                  <Button key={x.id} variant="outline" onClick={() => run(() => api.post(`/tables/${t.id}/join`, { target_table_id: x.id }), "Mesas juntadas")} data-testid={`join-target-${x.number}`}>{x.number}</Button>
                ))}
              </div>
            )}
          </div>
        ) : st === "limpeza" ? (
          <Button className="w-full" onClick={() => run(() => api.post(`/tables/${t.id}/status`, { status: "livre" }), "Mesa liberada")} data-testid="finish-cleaning-btn"><Sparkles className="h-4 w-4 mr-1" />Limpeza concluída — liberar mesa</Button>
        ) : (
          <div className="space-y-3">
            <Field label="Nome do cliente (opcional)"><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="open-table-customer-input" /></Field>
            <Button className="w-full h-11" onClick={openTable} data-testid="confirm-open-table-btn">Abrir atendimento</Button>
            <div className="grid grid-cols-2 gap-2">
              {st === "reservada"
                ? <Button variant="outline" onClick={() => run(() => api.post(`/tables/${t.id}/status`, { status: "livre" }), "Reserva cancelada")} data-testid="toggle-reserve-table-btn">Cancelar reserva</Button>
                : <Button variant="outline" onClick={() => setMode("reserve")} data-testid="toggle-reserve-table-btn"><CalendarClock className="h-4 w-4 mr-1" />Reservar</Button>}
              <Button variant="outline" onClick={() => run(() => api.post(`/tables/${t.id}/status`, { status: "limpeza" }), "Mesa em limpeza")} data-testid="set-cleaning-btn"><Sparkles className="h-4 w-4 mr-1" />Em limpeza</Button>
            </div>
            {mode === "reserve" && (
              <div className="grid grid-cols-2 gap-2 rounded-lg border p-3" data-testid="reserve-form">
                <Field label="Nome"><Input value={res.name} onChange={(e) => setRes((r) => ({ ...r, name: e.target.value }))} data-testid="reserve-name-input" /></Field>
                <Field label="Telefone"><Input value={res.phone} onChange={(e) => setRes((r) => ({ ...r, phone: e.target.value }))} data-testid="reserve-phone-input" /></Field>
                <Field label="Data e hora"><Input type="datetime-local" value={res.at} onChange={(e) => setRes((r) => ({ ...r, at: e.target.value }))} data-testid="reserve-at-input" /></Field>
                <Field label="Pessoas"><Input type="number" value={res.people} onChange={(e) => setRes((r) => ({ ...r, people: e.target.value }))} data-testid="reserve-people-input" /></Field>
                <Button className="col-span-2" disabled={!res.name || !res.at} onClick={() => run(() => api.post(`/tables/${t.id}/reserve`, { ...res, people: Number(res.people) || 1, at: new Date(res.at).toISOString() }), "Mesa reservada")} data-testid="confirm-reserve-btn">Confirmar reserva</Button>
              </div>
            )}
          </div>
        )}
        <Button variant="ghost" onClick={showHistory} data-testid="table-history-btn"><History className="h-4 w-4 mr-1" />Histórico de ocupação</Button>
        {mode === "history" && (
          <ul className="divide-y rounded-lg border text-sm max-h-56 overflow-y-auto" data-testid="table-history-list">
            {history.map((h) => <li key={h.id} className="flex justify-between px-3 py-2"><span>#{h.comanda_number} · {fmtDateTime(h.opened_at)} → {fmtTime(h.closed_at)}</span><span className="font-mono">{brl(h.total)}</span></li>)}
            {!history.length && <li className="px-3 py-4 text-center text-muted-foreground">Sem ocupações registradas.</li>}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

const EMPTY = { number: "", name: "", seats: 4, shape: "square" };

export default function TablesFloor() {
  const [tables, setTables] = useState([]);
  const [view, setView] = useState(() => localStorage.getItem("pdv_tables_view") || "planta");
  const [editing, setEditing] = useState(false);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(null);
  const { session } = useAuth();
  const isManager = isManagerRole(session.user);
  const load = useCallback(() => api.get("/tables").then((r) => setTables(r.data)), []);
  useEffect(() => {
    load();
    const t = setInterval(() => !editing && load(), 20000);
    return () => clearInterval(t);
  }, [load, editing]);
  const switchView = (v) => { setView(v); localStorage.setItem("pdv_tables_view", v); };

  const saveLayout = async () => {
    try {
      await api.put("/tables/layout", { positions: tables.map((t, i) => ({ id: t.id, ...pos(t, i) })) });
      toast.success("Disposição salva");
      setEditing(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const saveTable = async () => {
    try {
      const body = { number: Number(form.number), name: form.name, seats: Number(form.seats) || 1, shape: form.shape };
      form.id ? await api.put(`/tables/${form.id}`, body) : await api.post("/tables", body);
      setForm(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const removeTable = async () => {
    if (!window.confirm(`Excluir a mesa ${form.number}?`)) return;
    try {
      await api.delete(`/tables/${form.id}`);
      setForm(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const counts = Object.keys(STATUS).map((k) => [k, tables.filter((t) => stateOf(t) === k).length]);

  return (
    <>
      <PageHeader eyebrow="Salão" title="Mesas" subtitle={`${tables.filter((t) => t.comanda).length} de ${tables.length} ocupadas`}
        actions={<>
          <div className="flex rounded-lg border p-1 bg-card">
            <button onClick={() => switchView("planta")} className={cn("rounded-md px-3 py-1.5 text-sm flex items-center gap-1", view === "planta" && "bg-primary text-primary-foreground")} data-testid="view-floor-btn"><Map className="h-4 w-4" />Planta</button>
            <button onClick={() => { switchView("grade"); setEditing(false); }} className={cn("rounded-md px-3 py-1.5 text-sm flex items-center gap-1", view === "grade" && "bg-primary text-primary-foreground")} data-testid="view-grid-btn"><Grid3x3 className="h-4 w-4" />Grade</button>
          </div>
          {isManager && view === "planta" && (editing
            ? <Button onClick={saveLayout} data-testid="save-layout-btn"><Save className="h-4 w-4 mr-1" />Salvar disposição</Button>
            : <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-layout-btn"><Move className="h-4 w-4 mr-1" />Editar disposição</Button>)}
          {isManager && <Button variant="outline" onClick={() => setForm(EMPTY)} data-testid="add-table-btn"><Plus className="h-4 w-4 mr-1" />Nova mesa</Button>}
        </>} />
      <SellBlocked />
      <div className="flex flex-wrap gap-4 text-xs" data-testid="table-legend">
        {counts.map(([k, n]) => <span key={k} className="flex items-center gap-2"><span className={cn("h-2.5 w-2.5 rounded-full", STATUS[k][2])} />{STATUS[k][0]} ({n})</span>)}
      </div>
      {editing && <p className="text-sm text-primary">Arraste as mesas para posicioná-las e clique em “Salvar disposição”. Clique no lápis da lista abaixo para editar nome, lugares e formato.</p>}
      {!tables.length ? <EmptyState icon={Grid3x3} title="Nenhuma mesa cadastrada" text="Cadastre as mesas do seu salão." /> : view === "planta" ? (
        <div className="overflow-x-auto"><div className="min-w-[760px]"><FloorPlan tables={tables} editing={editing} setTables={setTables} onClick={setSel} /></div></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-testid="tables-grid">
          {tables.map((t) => <TableTile key={t.id} t={t} onClick={() => setSel(t)} style={{ minHeight: 132 }} />)}
        </div>
      )}
      {editing && (
        <div className="flex flex-wrap gap-2">{tables.map((t) => <Button key={t.id} size="sm" variant="outline" onClick={() => setForm({ ...t })} data-testid={`edit-table-${t.number}`}><Pencil className="h-3 w-3 mr-1" />{t.number}</Button>)}</div>
      )}
      <TableDialog t={sel} tables={tables} onClose={() => setSel(null)} reload={load} />
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent data-testid="add-table-modal">
          <DialogHeader><DialogTitle>{form?.id ? `Editar mesa ${form.number}` : "Nova mesa"}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Número"><Input type="number" value={form.number} onChange={(e) => setForm((x) => ({ ...x, number: e.target.value }))} data-testid="new-table-number-input" /></Field>
              <Field label="Nome (opcional)"><Input value={form.name} onChange={(e) => setForm((x) => ({ ...x, name: e.target.value }))} placeholder="Ex.: Varanda 1" data-testid="new-table-name-input" /></Field>
              <Field label="Capacidade"><Input type="number" value={form.seats} onChange={(e) => setForm((x) => ({ ...x, seats: e.target.value }))} data-testid="new-table-seats-input" /></Field>
              <Field label="Formato">
                <Select value={form.shape} onValueChange={(v) => setForm((x) => ({ ...x, shape: v }))}>
                  <SelectTrigger data-testid="new-table-shape-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="square">Quadrada</SelectItem><SelectItem value="round">Redonda</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>
          )}
          <DialogFooter className="gap-2">
            {form?.id && <Button variant="outline" onClick={removeTable} data-testid="delete-table-btn"><Trash2 className="h-4 w-4 mr-1" />Excluir</Button>}
            <Button onClick={saveTable} disabled={!form?.number} data-testid="save-table-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
